import { useAuroraStore } from '../../store/auroraStore'
import type { AuroraState } from '../../store/auroraStore'
import { domainSummary, evidenceFor, type RubricSkill } from '../../store/selectors'
import { formatRelative } from '../../lib/dates'
import { domainColor, frameworkDomain, frameworkSkills } from '../../lib/rubric'
import { LevelBar } from './LevelBar'
import { TrendBadge } from './TrendBadge'
import { ConfidenceDot } from './ConfidenceDot'

/**
 * One domain, skill by skill.
 *
 * The rubric only holds skills somebody has observed. The framework holds every
 * skill the domain covers, so the two are merged here: what we know sits above
 * what we have not looked at, and neither is hidden. An unobserved skill is
 * still tappable — it opens the same page, which explains what the skill is and
 * how to check it. Nothing in this view is a dead end.
 */
export function DomainDetail({
  domainKey,
  onOpenSkill,
  childName,
}: {
  domainKey: string
  onOpenSkill: (skillKey: string) => void
  childName?: string
}) {
  const view = useAuroraStore((s: AuroraState) => readDomain(s, domainKey))
  if (!view) return null

  const { domain, observed, unobserved } = view

  return (
    <div className="domaindetail">
      <header
        className="domaindetail-head reveal"
        style={{ '--domain-color': domainColor(domainKey, 'var(--accent)'), '--i': 0 } as React.CSSProperties}
      >
        <h2 className="domaindetail-name">{domain.name}</h2>
        {domain.description && <p className="domaindetail-desc">{domain.description}</p>}

        {domain.observedCount > 0 && (
          <div className="domaindetail-figure">
            <span className="domaindetail-level tnum">{domain.level.toFixed(1)}</span>
            <span className="domaindetail-of">of 5 across {domain.observedCount} skills</span>
            <TrendBadge trend={domain.trend} size="sm" />
            <ConfidenceDot
              confidence={domain.confidence}
              observationCount={domain.evidenceCount}
              lastObservedAt={domain.lastObservedAt}
            />
          </div>
        )}
        <LevelBar
          level={domain.level}
          domainKey={domainKey}
          confidence={domain.confidence}
          size="lg"
        />
      </header>

      {observed.length > 0 && (
        <ul className="skilllist reveal" style={{ '--i': 1 } as React.CSSProperties}>
          {observed.map((row) => (
            <SkillRow key={row.key} row={row} domainKey={domainKey} onOpen={onOpenSkill} />
          ))}
        </ul>
      )}

      {unobserved.length > 0 && (
        <section className="section reveal" style={{ '--i': 2 } as React.CSSProperties}>
          <div className="section-head">
            <h3 className="section-title">Not looked at yet</h3>
          </div>
          <p className="skilllist-note">
            {observed.length > 0
              ? 'These are part of this area too. Open one to see what it looks like at each level.'
              : `Nothing logged in this area yet. Open any skill to see what it covers and how to check where ${childName ?? 'she'} is.`}
          </p>
          <ul className="skilllist is-quiet">
            {unobserved.map((row) => (
              <SkillRow key={row.key} row={row} domainKey={domainKey} onOpen={onOpenSkill} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function SkillRow({
  row,
  domainKey,
  onOpen,
}: {
  row: SkillRowData
  domainKey: string
  onOpen: (skillKey: string) => void
}) {
  const measured = typeof row.level === 'number'

  return (
    <li>
      <button
        className={`skillrow${measured ? '' : ' is-unmeasured'}`}
        onClick={() => onOpen(row.key)}
        aria-label={
          measured
            ? `${row.name}, level ${row.level} of 5`
            : `${row.name}, not looked at yet`
        }
      >
        <span className="skillrow-main">
          <span className="skillrow-name">
            {row.name}
            {row.parentOverride && <span className="skillrow-yours">yours</span>}
          </span>
          {row.note && <span className="skillrow-note">{row.note}</span>}
          <span className="skillrow-when">
            {row.lastObservedAt
              ? `Last seen ${formatRelative(row.lastObservedAt)}`
              : 'Not observed yet'}
          </span>
        </span>

        <span className="skillrow-right">
          {measured ? (
            <>
              <span className="skillrow-level tnum">{row.level}</span>
              <LevelBar
                level={row.level}
                domainKey={domainKey}
                confidence={row.confidence}
                size="sm"
              />
              <TrendBadge trend={row.trend} size="sm" showLabel={false} />
            </>
          ) : (
            <span className="skillrow-level is-blank" aria-hidden="true">
              —
            </span>
          )}
          <span className="skillrow-chevron" aria-hidden="true">
            <ChevronIcon />
          </span>
        </span>
      </button>
    </li>
  )
}

interface SkillRowData extends RubricSkill {
  lastObservedAt?: number
}

/**
 * Merge what the rubric holds with what the framework covers, in one pass over
 * the store so the whole list costs a single selector rather than one per row.
 */
function readDomain(s: AuroraState, domainKey: string) {
  const domain = domainSummary(s, domainKey)
  if (!domain) return undefined

  const known = frameworkSkills(domainKey)
  const byKey = new Map(domain.skills.map((sk) => [sk.key, sk]))

  const rows: SkillRowData[] = []
  const seen = new Set<string>()
  const push = (sk: RubricSkill) => {
    if (seen.has(sk.key)) return
    seen.add(sk.key)
    rows.push({ ...sk, lastObservedAt: evidenceFor(s, domainKey, sk.key)[0]?.at })
  }

  // Framework order first — a stable reading order that does not shuffle as
  // observations land — then anything the agent added that we do not know.
  for (const skill of known) push(byKey.get(skill.key) ?? { key: skill.key, name: skill.name })
  for (const sk of domain.skills) push(sk)

  return {
    domain: { ...domain, description: frameworkDomain(domainKey)?.description },
    observed: rows.filter((r) => typeof r.level === 'number'),
    unobserved: rows.filter((r) => typeof r.level !== 'number'),
  }
}

const ChevronIcon = () => (
  <svg
    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
)
